import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import {
  productRequestsApi,
  ProductRequest,
  ProductRequestStatus,
} from '../../api/product-requests';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { ImageLightbox } from '../../components/ui/ImageLightbox';
import { useToast } from '../../hooks/useToast';
import { shopApi, AdminProduct } from '../../api/shop';

const statusColors: Record<
  ProductRequestStatus,
  'success' | 'danger' | 'warning' | 'info' | 'neutral'
> = {
  pending: 'warning',
  sourcing: 'info',
  found: 'success',
  unavailable: 'danger',
  fulfilled: 'neutral',
  cancelled: 'neutral',
};

const STATUS_OPTIONS: ProductRequestStatus[] = [
  'pending',
  'sourcing',
  'found',
  'unavailable',
  'fulfilled',
  'cancelled',
];

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const ProductRequests: React.FC = () => {
  usePage('Product Requests');
  const toast = useToast();

  const [data, setData] = useState<ProductRequest[]>([]);
  const [meta, setMeta] = useState<{ page: number; totalPages: number } | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ProductRequestStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ProductRequest | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await productRequestsApi.list({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setData(result.items);
      setMeta(result.meta);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filterSelectStyle: React.CSSProperties = {
    height: 38,
    padding: '0 32px 0 12px',
    fontSize: 13,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-700)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProductRequestStatus | '')}
          style={filterSelectStyle}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner size={28} />
        </div>
      ) : (
        <Card padding={0}>
          <Table
            columns={[
              {
                key: 'image',
                header: 'Image',
                width: '80px',
                render: (r: ProductRequest) =>
                  r.imageUrls?.length ? (
                    <div style={{ position: 'relative', width: 48, height: 48 }}>
                      <img
                        src={r.imageUrls[0]}
                        alt="requested product"
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--neutral-300)',
                        }}
                      />
                      {r.imageUrls.length > 1 && (
                        <span
                          style={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 'var(--radius-full)',
                            padding: '1px 5px',
                          }}
                        >
                          +{r.imageUrls.length - 1}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--neutral-500)', fontSize: 12 }}>—</span>
                  ),
              },
              {
                key: 'note',
                header: 'Request',
                render: (r: ProductRequest) => (
                  <span style={{ color: r.note ? 'var(--neutral-800)' : 'var(--neutral-500)' }}>
                    {r.note || 'No description'}
                  </span>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (r: ProductRequest) => (
                  <Badge variant={statusColors[r.status]}>{r.status}</Badge>
                ),
              },
              {
                key: 'createdAt',
                header: 'Requested',
                render: (r: ProductRequest) => formatDate(r.createdAt),
              },
              {
                key: 'action',
                header: '',
                render: (r: ProductRequest) => (
                  <Button size="sm" variant="secondary" onClick={() => setSelected(r)}>
                    Manage
                  </Button>
                ),
              },
            ]}
            data={data}
            emptyMessage="No product requests yet"
          />
          {meta && meta.totalPages > 1 && (
            <div style={{ padding: 16 }}>
              <Pagination
                page={meta.page}
                totalPages={meta.totalPages}
                total={0}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      )}

      {selected && (
        <ManageModal
          request={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            void fetchData();
          }}
          toast={toast}
        />
      )}
    </>
  );
};

const ManageModal: React.FC<{
  request: ProductRequest;
  onClose: () => void;
  onSaved: () => void;
  toast: { success: (msg: string) => void; error: (err: unknown, fallback?: string) => void };
}> = ({ request, onClose, onSaved, toast }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [status, setStatus] = useState<ProductRequestStatus>(request.status);
  const [adminNote, setAdminNote] = useState(request.adminNote ?? '');
  const [matchedProductId, setMatchedProductId] = useState(request.matchedProductId ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await productRequestsApi.update(request.id, {
        status,
        adminNote: adminNote.trim() || undefined,
        matchedProductId: matchedProductId.trim() || undefined,
      });
      const resolved = status === 'found' || status === 'unavailable';
      toast.success(
        resolved ? 'Saved — the buyer has been notified' : 'Request updated',
      );
      onSaved();
    } catch (err) {
      toast.error(err, 'Failed to update request');
    } finally {
      setSaving(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: 40,
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-800)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
  };

  return (
    <Modal isOpen onClose={onClose} title="Manage product request" maxWidth={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {request.imageUrls?.length ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {request.imageUrls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`requested product ${i + 1}`}
                onClick={() => setLightboxIndex(i)}
                style={{
                  width: 96,
                  height: 96,
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  background: 'var(--neutral-200)',
                  cursor: 'zoom-in',
                }}
              />
            ))}
          </div>
        ) : null}
        {request.note && (
          <div>
            <div style={{ fontSize: 12, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
              Buyer's description
            </div>
            <div style={{ fontSize: 14, color: 'var(--neutral-800)' }}>{request.note}</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ProductRequestStatus)}
            style={selectStyle}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            Setting <strong>Found</strong> or <strong>Unavailable</strong> notifies the buyer (push + email).
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
            Matched product (optional)
          </label>
          <ProductPicker value={matchedProductId} onChange={setMatchedProductId} />
          <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            Link a catalog product so the buyer can order it directly.
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
            Note to buyer (optional)
          </label>
          <textarea
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="e.g. We found it — search 'X' in the shop."
            style={{ ...selectStyle, height: 'auto', resize: 'vertical', minHeight: 80 }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save
          </Button>
        </div>
      </div>
      {lightboxIndex !== null && (
        <ImageLightbox
          images={request.imageUrls}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </Modal>
  );
};

/** Search-as-you-type product picker. Stores the selected product's id via
 *  onChange; shows the chosen product (image + name + price) with a clear
 *  control. Debounced so typing doesn't hammer the API. */
const ProductPicker: React.FC<{
  value: string;
  onChange: (productId: string) => void;
}> = ({ value, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<AdminProduct | null>(null);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const items = await shopApi.searchProducts(query, 10);
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, open]);

  // Selected product chosen this session, or a pre-existing linked id.
  if (selected || (value && !open)) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 10,
          border: '1px solid var(--neutral-300)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--neutral-200)',
        }}
      >
        {selected?.imageUrl ? (
          <img
            src={selected.imageUrl}
            alt={selected.name}
            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
          />
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selected ? selected.name : 'Linked product'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            {selected ? `₦${Math.round(selected.price).toLocaleString('en-NG')}` : value}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setSelected(null);
            onChange('');
            setQuery('');
            setOpen(true);
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Input
        placeholder="Search products by name…"
        value={query}
        autoFocus
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      {open && (query.length > 0 || results.length > 0) && (
        <div
          style={{
            marginTop: 6,
            border: '1px solid var(--neutral-300)',
            borderRadius: 'var(--radius-md)',
            maxHeight: 220,
            overflowY: 'auto',
            background: 'var(--neutral-100)',
          }}
        >
          {loading ? (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <Spinner size={18} />
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: 'var(--neutral-500)' }}>
              No products found
            </div>
          ) : (
            results.map((p) => (
              <div
                key={p.id}
                onClick={() => {
                  setSelected(p);
                  onChange(p.id);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: 10,
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--neutral-200)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--blue-100)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.name}
                    style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                  />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: 'var(--neutral-200)' }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    ₦{Math.round(p.price).toLocaleString('en-NG')}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
